import { useEffect, useRef } from 'react';

/**
 * Рефетч данных при возврате на страницу после того, как она была скрыта.
 *
 * Зачем: пользователь сворачивает Mini App (или переключает вкладку браузера),
 * возвращается через какое-то время — данные на экране уже устарели.
 * Хук перезагружает текущую страницу при возврате, но только если она была
 * скрыта дольше порога `minHiddenMs` (чтобы не дёргать бэкенд при каждом
 * мелком переключении).
 *
 * Универсален: работает и в Telegram WebView, и в обычной браузерной вкладке,
 * т.к. построен на стандартном Page Visibility API (document.visibilitychange).
 * `pageshow` дополнительно покрывает возврат из bfcache (Safari/iOS).
 *
 * @param {Function} onVisible  колбэк рефетча (обычно fetch с { silent: true })
 * @param {Object}   [options]
 * @param {number}   [options.minHiddenMs=30000]  не рефетчить, если скрыто меньше этого времени (мс)
 * @param {boolean}  [options.enabled=true]       можно отключить (напр. в режиме редактирования)
 */
export default function useRefreshOnVisible(onVisible, options = {}) {
  const { minHiddenMs = 30000, enabled = true } = options;

  // Держим колбэк в ref, чтобы не переподписываться на каждый рендер.
  const cbRef = useRef(onVisible);
  cbRef.current = onVisible;

  // Момент, когда страница стала скрытой.
  const hiddenAtRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const handleHidden = () => {
      hiddenAtRef.current = Date.now();
    };

    const handleVisible = () => {
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt == null) return;            // не были скрыты — пропускаем
      if (Date.now() - hiddenAt < minHiddenMs) return; // скрыто слишком недолго
      cbRef.current?.();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') handleHidden();
      else handleVisible();
    };

    // Возврат из bfcache: persisted === true означает, что страница ожила из кэша.
    const onPageShow = (e) => {
      if (e.persisted) handleVisible();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [enabled, minHiddenMs]);
}
